import React, { useState } from 'react';
import {
	Copy,
	ExternalLink,
	MapPin,
	Phone,
	Printer,
	Building2,
	User,
	Clock,
	CheckCircle,
	StickyNote,
	Activity,
	Hospital,
	List,
	Check,
	MessageCircle
} from 'lucide-react';
import { PageLayout } from '~/shared/PageLayout';
import { useSuspenseSearch } from '~/util/http/useSuspenseSearch';
import { SectionContainer } from '~/shared/section/SectionContainer';
import { useNavigate } from 'react-router-dom';
import { getPatientLegacyHospitalizationRoute } from '../util';
import { useMutation } from '~/util/http/useMutation';
import { type ConfirmHospitalizationEventCommand, confirmHospitalizationEventOptions, sendHospitalizationMessageOptions } from '@generated';
import { formatPhoneNumber } from '~/util/functions';
import { formatToDateTwoYearAndTimeOrNull } from '~/util/date';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '~/shared/components/buttons/Button';
import { getSearchQueryKey } from '~/util/http/useSearch';

function DetailField({ label, value, onCopy, icon: Icon = Copy }: { label: string; value: string; onCopy?: () => void; icon?: React.ElementType }) {
	return (
		<div className="border-b border-gray-100 py-3 last:border-b-0">
			<div className="mb-1 flex items-center justify-between">
				<span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
				{onCopy && (
					<button
						type="button"
						onClick={onCopy}
						aria-label={`Copy ${label}`}
						className="rounded p-1 transition-colors hover:bg-primary-500/10"
					>
						<Icon className="size-3.5 text-gray-400" />
					</button>
				)}
			</div>
			<div className="text-sm text-gray-900">{value || '—'}</div>
		</div>
	);
}

export function PatientsLegacyHospitalizations({ sponsorMrn, stayUuid }: { sponsorMrn: string; stayUuid: string }) {
	const markAsConfirmed = useMutation<void, ConfirmHospitalizationEventCommand>(confirmHospitalizationEventOptions);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data } = useSuspenseSearch({
		searchType: 'careport-stays',
		filters: [{ name: 'stayUuid', operator: '==', value: stayUuid }],
		pageSize: 1
	});

	const { data: last3Stays } = useSuspenseSearch({
		searchType: 'careport-stays',
		filters: [{ name: 'sponsorMrn', operator: '==', value: sponsorMrn }],
		sortColumn: 'lastStatusDateTime',
		sortDirection: 'desc',
		pageSize: 3
	});
	const { data: visitEvents } = useSuspenseSearch({
		searchType: 'careport-visits',
		filters: [{ name: 'stayUuid', operator: '==', value: stayUuid }],
		sortColumn: 'statusDateTime',
		sortDirection: 'desc',
		pageSize: 50
	});

	const sendTeamsMessage = useMutation(sendHospitalizationMessageOptions);
	const selectedStay = data.find(stay => stay.stayUuid === stayUuid);
	const [alertTeam, setAlertTeam] = useState<{ statusUpdate: boolean; message: string }>({ statusUpdate: false, message: '' });

	if (!selectedStay) {
		throw new Error('Hospitalization stay not found for the given sponsorMrn and stayUuid');
	}

	const onAlertTeam = () => {
		sendTeamsMessage.mutate(
			{
				stayUuid,
				message: alertTeam.message,
				statusUpdate: alertTeam.statusUpdate
			},
			{
				onSuccess: () => {
					setAlertTeam({ statusUpdate: false, message: '' });
				}
			}
		);
	};

	const onMarkAsConfirmed = (eventId: string) => {
		markAsConfirmed.mutate(
			{ eventId },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: getSearchQueryKey('careport-stays') });
					queryClient.invalidateQueries({ queryKey: getSearchQueryKey('careport-visits') });
				}
			}
		);
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	const facilityFullAddress = [
		selectedStay.facilityAddress,
		`${selectedStay.facilityCity}, ${selectedStay.faciltyState} ${selectedStay.facilityZipCode}`
	]
		.filter(Boolean)
		.join(', ');

	return (
		<PageLayout
			pageHeader={{
				title: 'Patient Hospitalizations',
				TitleIcon: Hospital,
				backUrl: '/patienthospitalizations'
			}}
		>
			<div className="mx-auto max-w-[1440px]">
				<div className="mb-6 flex items-center gap-3">
					<div className="rounded-lg bg-blue-50 p-2">
						<Activity className="size-6 text-blue-600" />
					</div>
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">
							Hospitalizations for {selectedStay.patientName} (#{selectedStay.sponsorMrn})
						</h1>
						<p className="text-sm text-gray-600">Track and manage patient hospital events and status updates</p>
					</div>
				</div>

				<div className="grid grid-cols-12 gap-6">
					<div className="col-span-12 gap-6 space-y-6 lg:col-span-3">
						<SectionContainer TitleIcon={User} title="Patient Details">
							<div className="px-4 py-2">
								<DetailField label="Name" value={selectedStay.patientName} onCopy={() => copyToClipboard(selectedStay.patientName)} />
								<DetailField label="MR #" value={selectedStay.sponsorMrn} onCopy={() => copyToClipboard(selectedStay.sponsorMrn)} />
								<DetailField
									label="Team"
									value={selectedStay.patientTeam ?? 'N/A'}
									onCopy={() => copyToClipboard(selectedStay.patientTeam ?? 'N/A')}
								/>
								<DetailField
									label="Case Manager"
									value={selectedStay.caseManagerName ?? 'N/A'}
									onCopy={() => copyToClipboard(selectedStay.caseManagerName ?? 'N/A')}
								/>
							</div>
						</SectionContainer>
						<SectionContainer TitleIcon={Clock} title="Last 3 Stays">
							<div className="divide-y divide-gray-100">
								{last3Stays.map(entry => (
									<button
										key={entry.stayUuid}
										type="button"
										onClick={() => navigate(getPatientLegacyHospitalizationRoute(entry.sponsorMrn, entry.stayUuid))}
										className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
											selectedStay.stayUuid === entry.stayUuid
												? 'border-l-4 border-l-primary-500 bg-primary-500/5'
												: 'border-l-4 border-l-transparent hover:bg-gray-50'
										}`}
									>
										<span
											className={`text-sm font-medium ${
												selectedStay.stayUuid === entry.stayUuid ? 'text-primary-500' : 'text-gray-900'
											}`}
										>
											{entry.lastStatusDateTime.toLocaleDateString()} - {entry.lastStatus}
										</span>
										{selectedStay.stayUuid === entry.stayUuid && (
											<span className="rounded-full bg-primary-500/10 px-2 py-0.5 text-xs font-medium text-primary-500">
												Active
											</span>
										)}
									</button>
								))}
							</div>
						</SectionContainer>
					</div>

					{/* Center - Hospitalizations */}
					<div className="col-span-12 space-y-6 lg:col-span-6">
						{/* Hospitalization Details */}
						<SectionContainer TitleIcon={List} title="Hospitalization Details">
							<div className="border-b border-gray-200 p-4">
								<div className="grid grid-cols-2 gap-4">
									<div>
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Careport URL</span>
										<div className="mt-1 flex items-center gap-2">
											<a
												href={selectedStay.timelineUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="truncate text-sm text-blue-600 transition-colors hover:text-blue-800"
											>
												{selectedStay.timelineUrl}
											</a>
											<ExternalLink className="size-4 shrink-0 text-gray-400" />
										</div>
									</div>
									<div>
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Referring Hospital</span>
										<p className="mt-1 text-sm text-gray-900">{selectedStay.referringHospitalName ?? 'N/A'}</p>
									</div>
								</div>
							</div>

							<div className="border-b border-gray-200 bg-gray-50 p-4">
								<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Current Status</span>
								<p className="mt-1 text-sm font-medium text-gray-900">{selectedStay.lastStatus}</p>
							</div>

							<div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200">
								<div className="p-4">
									<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Primary Diagnosis</span>
									<p className="mt-1 text-sm font-medium text-gray-900">
										{selectedStay.primaryDiagnosisCode ? selectedStay.primaryDiagnosisCode : 'N/A'}
									</p>
								</div>
								<div className="p-4">
									<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Discharge Location</span>
									<p className="mt-1 text-sm font-medium text-gray-900">{selectedStay.dischargeLocation}</p>
								</div>
								<div className="p-4">
									<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Discharge Disposition</span>
									<p className="mt-1 text-sm text-gray-900">{selectedStay.dischargeDisposition || '—'}</p>
								</div>
							</div>
						</SectionContainer>

						{/* Events Table */}
						<div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead className="border-b border-gray-200 bg-gray-50">
										<tr>
											<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
												Status Date/Time
											</th>
											<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
												Status
											</th>
											<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
												Setting
											</th>
											<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
												Confirmed
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{visitEvents.map(event => (
											<tr key={event.id} className="transition-colors hover:bg-gray-50">
												<td className="px-6 py-4 text-sm text-gray-900">
													{formatToDateTwoYearAndTimeOrNull(event.statusDateTime, '-')}
												</td>
												<td className="px-6 py-4 text-sm text-gray-900">{event.status}</td>
												<td className="px-6 py-4">
													<span className="inline-flex items-center rounded-md bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
														{event.setting ?? 'N/A'}
													</span>
												</td>
												<td className="px-6 py-4">
													{event.confirmed ? (
														<span className="inline-flex items-center gap-1.5 rounded-md bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
															<CheckCircle className="size-3.5" />
															Confirmed
														</span>
													) : (
														<Button
															onClick={() => onMarkAsConfirmed(event.id)}
															Icon={Check}
															label="Confirm"
															isPending={markAsConfirmed.isPending && markAsConfirmed.variables?.eventId === event.id}
														/>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>

						{/* Notes */}
						<SectionContainer
							TitleIcon={StickyNote}
							title="Notes"
							headerRightSlot={
								<label className="flex cursor-pointer items-center gap-2">
									<input
										type="checkbox"
										checked={alertTeam.statusUpdate}
										onChange={e => setAlertTeam(prev => ({ ...prev, statusUpdate: e.target.checked }))}
										className="size-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
									/>
									<span className="text-sm text-gray-700">Include Status</span>
								</label>
							}
						>
							<div className="p-4">
								<textarea
									value={alertTeam.message}
									onChange={e => setAlertTeam(prev => ({ ...prev, message: e.target.value }))}
									placeholder="Add notes about this hospitalization..."
									rows={5}
									className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
								/>
								<div className="mt-3 flex justify-end">
									<Button
										onClick={() => onAlertTeam()}
										Icon={MessageCircle}
										label="Send Alert to Team"
										isPending={sendTeamsMessage.isPending}
										isDisabled={!alertTeam.message.trim()}
									/>
								</div>
							</div>
						</SectionContainer>
					</div>

					{/* Right Sidebar - Facility Details */}
					<div className="col-span-12 lg:col-span-3">
						<SectionContainer TitleIcon={Building2} title="Facility Details">
							<div className="px-4 py-2">
								<DetailField
									label="Name"
									value={selectedStay.facilityName ?? 'N/A'}
									onCopy={() => copyToClipboard(selectedStay.facilityName ?? 'N/A')}
								/>
								{selectedStay.facilityType && (
									<DetailField
										label="Type"
										value={selectedStay.facilityType}
										onCopy={() => copyToClipboard(selectedStay.facilityType ?? 'N/A')}
									/>
								)}
								{selectedStay.facilityPhoneNumber && (
									<DetailField
										label="Phone Number"
										value={formatPhoneNumber(selectedStay.facilityPhoneNumber)}
										icon={Phone}
										onCopy={() => copyToClipboard(selectedStay.facilityPhoneNumber!)}
									/>
								)}
								{selectedStay.facilityFaxNumber && (
									<DetailField
										label="Fax Number"
										value={formatPhoneNumber(selectedStay.facilityFaxNumber)}
										icon={Printer}
										onCopy={() => copyToClipboard(selectedStay.facilityFaxNumber!)}
									/>
								)}
								<div className="border-b border-gray-100 py-3">
									<div className="mb-1 flex items-center justify-between">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">Address</span>
										<button
											type="button"
											aria-label="Copy Address"
											onClick={() => copyToClipboard(facilityFullAddress)}
											className="rounded p-1 transition-colors hover:bg-primary-500/10"
										>
											<MapPin className="size-3.5 text-gray-400" />
										</button>
									</div>
									<div className="text-sm text-gray-900">{selectedStay.facilityAddress}</div>
									<div className="text-sm text-gray-600">
										{selectedStay.facilityCity}, {selectedStay.faciltyState} {selectedStay.facilityZipCode}
									</div>
								</div>
							</div>
						</SectionContainer>

						<div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
							<div className="flex h-56 flex-col items-center justify-center bg-gray-100">
								<MapPin className="mb-2 size-10 text-gray-300" />
								<p className="text-sm font-medium text-gray-500">Map Location</p>
								<p className="mt-1 text-xs text-gray-400">{selectedStay.facilityAddress}</p>
								<p className="text-xs text-gray-400">
									{selectedStay.facilityCity}, {selectedStay.faciltyState} {selectedStay.facilityZipCode}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</PageLayout>
	);
}
